import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./SubscriptionPage.module.css";

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [selectedDiscountInfo, setSelectedDiscountInfo] = useState(null); // Store discount info
  const [loading, setLoading] = useState(true);

  //hunesh-- while testing i made it true so i can redirect to next page
  const [subscribing, setSubscribing] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("monthly");
  const { currentUser, isAuthenticated, fetchAndUpdateUser } = useAuth();
  const navigate = useNavigate();
  const [hasHandledSuccess, setHasHandledSuccess] = useState(false);

  const parseDescriptionToFeatures = (description) => {
    if (!description) return [];
    const features = description
      .split(".")
      .map((part) => part.trim())
      .filter((part) => part)
      .flatMap((part) => part.split("+").map((subPart) => subPart.trim()))
      .filter((subPart) => subPart);

    return features;
  };
  // Helper function to parse description and extract all data based on active tab
  const parsePlanDescription = (description, isYearly) => {
    if (!description) return null;
    const targetType = isYearly ? "yearly" : "monthly";

    // Split by newlines to separate monthly and yearly sections
    const sections = description.split(/\n+/);

    // Find the section that matches the target type
    const targetSection =
      sections.find((section) =>
        section.toLowerCase().includes(`for ${targetType}`),
      ) || description;

    // Extract matching limit: "up to X active matchings/mth"
    const limitMatch = targetSection.match(
      /up to (\d+) active matchings\/mth/i,
    );
    const matchingLimit = limitMatch ? limitMatch[1] : null;

    // Extract matching price: Handle both "active matchings at X.XX/each" and "active matchings X.XX/each"
    // Try with "at" first, then without "at"
    let priceMatch = targetSection.match(/active matchings at ([\d.]+)\/each/i);
    if (!priceMatch) {
      priceMatch = targetSection.match(/active matchings ([\d.]+)\/each/i);
    }
    const matchingPrice = priceMatch ? priceMatch[1] : null;

    return {
      matchingLimit,
      matchingPrice,
      hasUnlimitedListing: targetSection
        .toLowerCase()
        .includes("unlimited listing"),
    };
  };

  // Helper function to get matching price from description based on active tab
  const getMatchingPriceFromDescription = (description, isYearly) => {
    const parsed = parsePlanDescription(description, isYearly);
    if (!parsed?.matchingPrice && description) {
      // Debug: Log when price is not found
      console.warn(
        `Matching price not found in description for ${
          isYearly ? "yearly" : "monthly"
        }:`,
        description,
      );
    }
    return parsed?.matchingPrice || null;
  };

  // Helper function to get matching limit from description based on active tab
  const getMatchingLimitFromDescription = (description, isYearly) => {
    const parsed = parsePlanDescription(description, isYearly);
    return parsed?.matchingLimit || null;
  };

  // Helper function to get the correct price for a plan based on active tab
  const getPlanPrice = (plan) => {
    const targetInterval = activeTab === "monthly" ? "month" : "year";
    const price = plan.prices?.find(
      (p) => p.interval === targetInterval && p.isActive,
    );
    return price || plan.prices?.find((p) => p.isDefault);
  };

  // Helper function to format CHF prices with apostrophe thousand separators (Swiss format)
  const formatCHF = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return value;
    
    // Format with Swiss CHF format (apostrophe as thousand separator)
    const formatted = numberValue.toFixed(2);
    const parts = formatted.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return `${integerPart}.${parts[1]}`;
  };

  // Helper function to get display interval text (monthly/yearly)
  const getDisplayInterval = (interval) => {
    if (!interval) return activeTab === "monthly" ? t("monthly") : t("yearly");
    return interval === "month"
      ? t("monthly")
      : interval === "year"
        ? t("yearly")
        : interval;
  };

  // Helper function to get the correct price ID for a plan based on active tab
  const getPlanPriceId = (plan) => {
    const targetInterval = activeTab === "monthly" ? "month" : "year";
    const price = plan.prices?.find(
      (p) => p.interval === targetInterval && p.isActive,
    );
    return price?.id || plan.prices?.find((p) => p.isDefault)?.id;
  };

  useEffect(() => {
    const getUrlParameter = (name) => {
      name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
      const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
      const results = regex.exec(window.location.search);
      return results === null
        ? ""
        : decodeURIComponent(results[1].replace(/\+/g, " "));
    };

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await authFetch(`${BASE_URL}/subscription/plans`);
        const data = await res.json();
        if (res.ok && data && data.data) {
          setPlans(data.data);
          console.log("✅ Plans fetched from backend:");
          data.data.forEach((plan, i) => {
            console.log(`🟩 Plan ${i + 1}:`, plan);
          });
        } else {
          console.error(
            "❌ No plan data received or failed to fetch plans:",
            data.message || data,
          );
          toast.error("Failed to load subscription plans.");
        }
      } catch (err) {
        console.error("❌ Error fetching plans:", err);
        toast.error("Network error while fetching plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();

    const success = getUrlParameter("success");
    const canceled = getUrlParameter("cancel");
    const paymentGateway = getUrlParameter("paymentGateway"); // New: to distinguish Stripe/PayPal return
    // const sessionId = getUrlParameter("session_id"); // Stripe sessionId is handled by backend webhook

    if (success && !hasHandledSuccess) {
      setHasHandledSuccess(true); // Set the flag immediately to prevent re-runs
      setSubscriptionStatus("🥳 Your subscription was successful!");

      const pending = localStorage.getItem("pendingSubscription");
      if (pending) {
        console.log(
          "📦 Checkout success detected. Relying on webhook/confirmation for backend update.",
        );
      } else {
        console.warn(
          "Checkout success indicated, but no pending record. User may have refreshed page.",
        );
      }

      const handleSubscriptionSuccessRedirect = async () => {
        // Small delay to allow backend webhook to process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Now, we call the fixed fetchAndUpdateUser to get the new user data.
        const updatedUser = await fetchAndUpdateUser();

        // The logic here is now solid because we are sure `updatedUser` is correct.
        if (updatedUser && updatedUser.isSubscribed) {
          // Don't show toast here - it will be handled by SuccessPage
          if (updatedUser.userType === "demand") {
            navigate("/MySearch");
          } else if (updatedUser.userType === "offer") {
            navigate("/MyProperties");
          } else {
            navigate("/");
          }
        } else {
          console.warn(
            "User still not subscribed after refresh. Webhook/confirmation might be delayed or failed.",
          );
          toast.info(
            "Subscription processing. If not active shortly, please contact support.",
          );
          // Still navigate to prevent the loop, assuming the status will be updated soon.
          if (updatedUser) {
            if (updatedUser.userType === "demand") {
              navigate("/MySearch");
            } else if (updatedUser.userType === "offer") {
              navigate("/MyProperties");
            } else {
              navigate("/");
            }
          } else {
            navigate("/login");
          }
        }
        localStorage.removeItem("pendingSubscription");
      };

      handleSubscriptionSuccessRedirect();
    } else if (canceled) {
      setSubscriptionStatus(
        "😕 Your subscription was canceled. You can try again.",
      );
      localStorage.removeItem("pendingSubscription");
      fetchAndUpdateUser();
    }
  }, [fetchAndUpdateUser, navigate, hasHandledSuccess]);

  // Redirect already-subscribed users away from subscription page
  useEffect(() => {
    if (currentUser && !hasHandledSuccess) {
      // Check if user has an actual active subscription (not just isSubscribed flag)
      const hasActiveSubscription =
        currentUser.UserSubscription &&
        currentUser.UserSubscription.status === "active" &&
        new Date(currentUser.UserSubscription.endDate) > new Date();

      if (hasActiveSubscription) {
        console.log("User has active subscription. Redirecting away from subscription page.");
        toast.info(t("already_subscribed_toast") || "You already have an active subscription.");
        // Redirect based on user type
        if (currentUser.userType === "demand") {
          navigate("/MySearch");
        } else if (currentUser.userType === "offer") {
          navigate("/MyProperties");
        } else {
          navigate("/");
        }
      }
    }
  }, [currentUser, navigate, hasHandledSuccess, t]);

  // useEffect(() => {
  //   const getUrlParameter = (name) => {
  //     name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
  //     const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  //     const results = regex.exec(window.location.search);
  //     return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  //   };

  //   const fetchPlans = async () => {
  //     try {
  //       setLoading(true);
  //       const res = await authFetch(`${BASE_URL}/subscription/plans`);
  //       const data = await res.json();
  //       if (res.ok && data && data.data) {
  //         setPlans(data.data);
  //         console.log("✅ Plans fetched from backend:");
  //         data.data.forEach((plan, i) => {
  //           console.log(`🟩 Plan ${i + 1}:`, plan);
  //         });
  //       } else {
  //         console.error("❌ No plan data received or failed to fetch plans:", data.message || data);
  //         toast.error("Failed to load subscription plans.");
  //       }
  //     } catch (err) {
  //       console.error("❌ Error fetching plans:", err);
  //       toast.error("Network error while fetching plans.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchPlans();

  //   const success = getUrlParameter("success");
  //   const canceled = getUrlParameter("cancel");
  //   const sessionId = getUrlParameter("session_id");
  //   if (success) {
  //     setSubscriptionStatus("🥳 Your subscription was successful!");

  //     const pending = localStorage.getItem("pendingSubscription");
  //     if (pending) {
  //       console.log("📦 Stripe checkout successful. Relying on webhook for backend update.");
  //     } else {
  //       console.warn("Subscription success indicated, but no pending record. User may have refreshed page.");
  //     }

  //     const handleSubscriptionSuccessRedirect = async () => {
  //       await new Promise(resolve => setTimeout(resolve, 2000));

  //       const updatedUser = await fetchAndUpdateUser();

  //       if (updatedUser && updatedUser.isSubscribed) {
  //         toast.success("Your subscription is now active!");
  //         if (updatedUser.userType === "demand") {
  //           navigate('/MySearch');
  //         } else if (updatedUser.userType === "offer") {
  //           navigate('/MyProperties');
  //         } else {
  //           navigate('/');
  //         }
  //       } else {
  //         console.warn("User still not subscribed after refresh. Webhook might be delayed or failed.");
  //         toast.info("Subscription processing. If not active shortly, please contact support.");
  //         if (updatedUser) {
  //           if (updatedUser.userType === "demand") { navigate('/MySearch'); }
  //           else if (updatedUser.userType === "offer") { navigate('/MyProperties'); }
  //           else { navigate('/'); }
  //         } else {
  //           navigate('/login'); // If no user data, perhaps session expired
  //         }
  //       }
  //       localStorage.removeItem("pendingSubscription");
  //     };

  //     handleSubscriptionSuccessRedirect();

  //   } // end if (success)

  //   if (canceled) {
  //     setSubscriptionStatus("😕 Your subscription was canceled. You can try again.");
  //     localStorage.removeItem("pendingSubscription");
  //     fetchAndUpdateUser();
  //   }
  // }, [fetchAndUpdateUser, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      console.warn(
        "User is not authenticated. Subscription options might be limited.",
      );
    } else {
      console.log(
        "✅ User is authenticated in SubscriptionPage. Current User:",
        currentUser?.email,
      );
    }
  }, [isAuthenticated, currentUser]);

  // Clear selections when tab changes
  useEffect(() => {
    setSelectedPlanId(null);
    setSelectedPriceId(null);
    setSelectedDiscountInfo(null);
  }, [activeTab]);

  const handleSubscribe = async () => {
    console.log(selectedPlanId, selectedPriceId);
    if (!selectedPlanId || !selectedPriceId) {
      toast.error("Please select a plan first.");
      return;
    }
    // const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
    const selectedPlan = plans.find(
      (plan) =>
        plan.SubscriptionProviders?.find(
          (p) => p.paymentGateway.toLowerCase() == "stripe",
        )?.planId === selectedPlanId ||
        plan.SubscriptionProviders?.find((p) => p.paymentGateway === "PayPal")
          ?.planId === selectedPlanId,
    );
    if (!selectedPlan) {
      toast.error("Selected plan details not found. Please try again.");
      return;
    }

    const selectedPrice = selectedPlan?.prices?.find(
      (p) => p.id === selectedPriceId,
    );
    if (!selectedPrice) {
      toast.error("Selected price details not found. Please try again.");
      return;
    }

    navigate("/checkout-summary", {
      state: {
        selectedPlan: selectedPlan,
        selectedPrice: selectedPrice,
        activeTab: activeTab,
        discountInfo: selectedDiscountInfo, // Pass discount information
      },
    });
  };

  //   if (!isAuthenticated || !currentUser || !currentUser.id || !currentUser.email) {
  //     toast.error("User not found or not logged in. Please log in again.");
  //     console.error("❌ No valid user found from AuthContext for subscription.");
  //     navigate('/login');
  //     return;
  //   }

  //   setSubscribing(true);

  //   const payload = {
  //     planId: selectedPlanId,
  //     userId: currentUser.id,
  //     email: currentUser.email,
  //     platform: "web",
  //     successUrl: `${window.location.origin}/subscription?success=true`,
  //     cancelUrl: `${window.location.origin}/subscription?cancel=true`,
  //   };

  //   console.log("📤 Subscribing with payload:", payload);

  //   try {
  //     const response = await authFetch(`${BASE_URL}/subscription/create-stripe`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(payload),
  //     });

  //     const data = await response.json();
  //     console.log("🎯 Response from /create-stripe:", data);

  //     if (!response.ok || !data.url) {
  //       console.error("❌ Stripe session creation failed. Backend response:", data);
  //       toast.error(data.message || "Subscription failed. Try again.");
  //       return;
  //     }

  //     localStorage.setItem("pendingSubscription", JSON.stringify({ planId: selectedPlanId }));
  //     console.log("✅ Saved pending planId in localStorage:", selectedPlanId);

  //     // Redirect to Stripe checkout
  //     window.location.href = data.url;
  //   } catch (err) {
  //     console.error("❌ Error starting subscription:", err);
  //     toast.error("Failed to initiate Stripe checkout. Please check your connection.");
  //   } finally {
  //     setSubscribing(false);
  //   }
  // };

  // const monthlyPlans = plans.filter((plan) => plan.interval === "month");
  // const yearlyPlans = plans.filter((plan) => plan.interval === "year");
  const monthlyPlans = plans.filter((plan) =>
    plan?.prices?.some((price) => price.interval === "month" && price.isActive),
  );

  // Extract yearly plans
  const yearlyPlans = plans.filter((plan) =>
    plan?.prices?.some((price) => price.interval === "year" && price.isActive),
  );

  // Helper function for plan card class names — color by position, not name
  const getPlanCardClass = (index, isSelected) => {
    const baseClass = styles.planCard;
    const selectedClass = isSelected ? styles.planCardSelected : "";

    const colorClasses = [
      styles.planCardGold,
      styles.planCardSilver,
      styles.planCardGold,
      styles.planCardPlatinum,
    ];
    const colorClass = colorClasses[index] ?? styles.planCardGold;

    return `${baseClass} ${colorClass} ${selectedClass}`.trim();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.subtitle}>
          {t("Subscription")}
        </h2>
        <h1 className={styles.title}>
          {t("Subscribe_Your")}{" "}
          <span>{t("Membership")}</span>
        </h1>
        {subscriptionStatus && (
          <div className={`${styles.statusMessage} ${subscriptionStatus.includes("successful") ? styles.statusSuccess : styles.statusError}`}>
            {subscriptionStatus}
          </div>
        )}
      </div>

      <div className={styles.tabContainer}>
        <div
          onClick={() => setActiveTab("monthly")}
          className={`${styles.tab} ${activeTab === "monthly" ? styles.tabActive : ""}`}
        >
          {t("Monthly_Prepaid")}
        </div>
        <div
          onClick={() => setActiveTab("yearly")}
          className={`${styles.tab} ${activeTab === "yearly" ? styles.tabActive : ""}`}
        >
          {t("Yearly_Prepaid")}
        </div>
      </div>

      <div className={styles.plansContainer}>
        {loading ? (
          <div className={styles.loading}>
            Loading plans...
          </div>
        ) : (
          (activeTab === "monthly" ? monthlyPlans : yearlyPlans).map((plan, planIndex) => {
            const isMostPopular = planIndex === 1; // 2nd plan is always "most popular"
            const isYearly = activeTab === "yearly";

            // Calculate discount based on monthly package values (only for yearly)
            let discountPercentage = 0;
            let actualValue = null;
            let discountAmount = null;
            let showDiscount = false;
            const yearlyPrice = parseFloat(
              getPlanPrice(plan)?.amount || plan.price,
            );

            if (isYearly) {
              // Find the corresponding monthly plan
              const monthlyPlan = monthlyPlans.find(
                (p) => p.name === plan.name,
              );
              if (monthlyPlan) {
                // Get matching_value and matching_count from monthly plan
                const monthlyMatchingPrice = parseFloat(
                  getMatchingPriceFromDescription(
                    monthlyPlan.description,
                    false,
                  ) || 0,
                );
                const monthlyMatchingCount = parseInt(
                  getMatchingLimitFromDescription(
                    monthlyPlan.description,
                    false,
                  ) ||
                    monthlyPlan.matching_limit ||
                    0,
                );

                if (monthlyMatchingPrice > 0 && monthlyMatchingCount > 0) {
                  // Calculate actual value: matching_value * 12 * matching_count
                  actualValue =
                    monthlyMatchingPrice * 12 * monthlyMatchingCount;

                  // Calculate discount amount: actual_value - yearly_price
                  discountAmount = actualValue - yearlyPrice;

                  // Calculate discount percentage: (dis_amount / actual_value) * 100
                  if (actualValue > 0 && discountAmount > 0) {
                    discountPercentage = (discountAmount / actualValue) * 100;
                    showDiscount = true;
                  }
                }
              }
            }

            const originalPrice = actualValue || yearlyPrice;
            const discountedPrice = yearlyPrice;

            return (
              <label
                key={plan.id}
                className={getPlanCardClass(
                  planIndex,
                  selectedPlanId === plan?.SubscriptionProviders?.find((p) => p.paymentGateway?.toLowerCase() === "stripe")?.planId
                )}
                onClick={() => {
                  const planId = plan.SubscriptionProviders?.find(
                    (p) => p.paymentGateway?.toLowerCase() === "stripe",
                  )?.planId;
                  const priceId = getPlanPriceId(plan);

                  // Calculate discount info using monthly plan values
                  let calculatedDiscountPercentage = 0;
                  let calculatedActualValue = null;
                  const yearlyPrice = parseFloat(
                    getPlanPrice(plan)?.amount || plan.price,
                  );

                  if (isYearly) {
                    const monthlyPlan = monthlyPlans.find(
                      (p) => p.name === plan.name,
                    );
                    if (monthlyPlan) {
                      const monthlyMatchingPrice = parseFloat(
                        getMatchingPriceFromDescription(
                          monthlyPlan.description,
                          false,
                        ) || 0,
                      );
                      const monthlyMatchingCount = parseInt(
                        getMatchingLimitFromDescription(
                          monthlyPlan.description,
                          false,
                        ) ||
                          monthlyPlan.matching_limit ||
                          0,
                      );

                      if (
                        monthlyMatchingPrice > 0 &&
                        monthlyMatchingCount > 0
                      ) {
                        calculatedActualValue =
                          monthlyMatchingPrice * 12 * monthlyMatchingCount;
                        const discountAmount =
                          calculatedActualValue - yearlyPrice;
                        if (calculatedActualValue > 0 && discountAmount > 0) {
                          calculatedDiscountPercentage =
                            (discountAmount / calculatedActualValue) * 100;
                        }
                      }
                    }
                  }

                  setSelectedPlanId(planId);
                  setSelectedPriceId(priceId);
                  setSelectedDiscountInfo({
                    discountPercentage: calculatedDiscountPercentage,
                    originalPrice: calculatedActualValue || yearlyPrice,
                    discountedPrice: yearlyPrice,
                    hasDiscount: calculatedDiscountPercentage > 0,
                  });
                }}
              >
                {/* MOST POPULAR Banner - always the 2nd plan (index 1) */}
                {isMostPopular && (
                  <div className={styles.popularBanner}>
                    {t("most_popular")}
                  </div>
                )}

                <div className={`${styles.planContent} ${isMostPopular ? styles.planContentWithBanner : styles.planContentNoBanner}`}>
                  <div className={styles.planHeader}>
                    <div className={styles.planRadioContainer}>
                      <input
                        type="radio"
                        name="subscription"
                        className={styles.planRadio}
                        value={
                          plan.SubscriptionProviders?.find(
                            (p) => p.paymentGateway?.toLowerCase() === "stripe",
                          )?.planId
                        }
                        checked={
                          selectedPlanId ===
                          plan.SubscriptionProviders?.find(
                            (p) => p.paymentGateway?.toLowerCase() === "stripe",
                          )?.planId
                        }
                        onChange={() => {
                          const planId = plan.SubscriptionProviders?.find(
                            (p) => p.paymentGateway?.toLowerCase() === "stripe",
                          )?.planId;
                          const priceId = getPlanPriceId(plan);

                          // Calculate discount info using monthly plan values
                          let calculatedDiscountPercentage = 0;
                          let calculatedActualValue = null;
                          const yearlyPrice = parseFloat(
                            getPlanPrice(plan)?.amount || plan.price,
                          );

                          if (isYearly) {
                            const monthlyPlan = monthlyPlans.find(
                              (p) => p.name === plan.name,
                            );
                            if (monthlyPlan) {
                              const monthlyMatchingPrice = parseFloat(
                                getMatchingPriceFromDescription(
                                  monthlyPlan.description,
                                  false,
                                ) || 0,
                              );
                              const monthlyMatchingCount = parseInt(
                                getMatchingLimitFromDescription(
                                  monthlyPlan.description,
                                  false,
                                ) ||
                                  monthlyPlan.matching_limit ||
                                  0,
                              );

                              if (
                                monthlyMatchingPrice > 0 &&
                                monthlyMatchingCount > 0
                              ) {
                                calculatedActualValue =
                                  monthlyMatchingPrice *
                                  12 *
                                  monthlyMatchingCount;
                                const discountAmount =
                                  calculatedActualValue - yearlyPrice;
                                if (
                                  calculatedActualValue > 0 &&
                                  discountAmount > 0
                                ) {
                                  calculatedDiscountPercentage =
                                    (discountAmount / calculatedActualValue) *
                                    100;
                                }
                              }
                            }
                          }

                          setSelectedPlanId(planId);
                          setSelectedPriceId(priceId);
                          setSelectedDiscountInfo({
                            discountPercentage: calculatedDiscountPercentage,
                            originalPrice: calculatedActualValue || yearlyPrice,
                            discountedPrice: yearlyPrice,
                            hasDiscount: calculatedDiscountPercentage > 0,
                          });
                        }}
                      />
                      <h2 className={styles.planName}>
                        {plan.name}
                      </h2>
                    </div>
                    {/* Discount Badge */}
                    {showDiscount && discountPercentage > 0 && (
                      <div className={styles.discountBadge}>
                        {Math.round(discountPercentage)}% {t("off")}
                      </div>
                    )}
                  </div>
                  <ul className={styles.featuresList}>
                    <li>{t("unlimited_listing")}</li>
                    <li>
                      + {t("upto")}{" "}
                      {getMatchingLimitFromDescription(
                        plan.description,
                        isYearly,
                      ) ||
                        plan.matching_limit ||
                        plan.matchingCount ||
                        ""}{" "}
                      {t("active_matchings_per_month")}
                    </li>
                    <li>
                      {getMatchingPriceFromDescription(
                        plan.description,
                        isYearly,
                      ) ? (
                        <>
                          {t("matchings_at")}{" "}
                          <b>
                            {getMatchingPriceFromDescription(
                              plan.description,
                              isYearly,
                            )}{" "}
                            {plan.currency || "CHF"}
                          </b>
                          /{t("each")}
                        </>
                      ) : (
                        <>{t("additional_matchings_price_varies")}</>
                      )}
                    </li>
                  </ul>
                  <div className={styles.priceSection}>
                    {showDiscount ? (
                      <>
                        <div>
                          <span className={styles.originalPrice}>
                            CHF {formatCHF(actualValue)}/
                            {getDisplayInterval(getPlanPrice(plan)?.interval)}
                          </span>
                        </div>
                        <div className={styles.discountedPrice}>
                          CHF {formatCHF(discountedPrice)}/
                          {getDisplayInterval(getPlanPrice(plan)?.interval)}
                        </div>
                        {/* <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginBottom: "12px",
                          }}
                        >
                          {t("plus_3_months_free")}
                        </div> */}
                        {/* <button
                          style={{
                            width: "100%",
                            padding: "8px",
                            backgroundColor: "#E9D5FF",
                            color: "#000",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "8px",
                            cursor: "pointer",
                          }}
                        >
                          Limited time deal
                        </button>
                        <button
                          style={{
                            width: "100%",
                            padding: "10px",
                            backgroundColor: "#000",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          Choose plan
                        </button>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            marginTop: "12px",
                            lineHeight: "1.4",
                          }}
                        >
                          Get 48 months for CHF {(parseFloat(discountedPrice) * 48).toFixed(2)} (regular price CHF {(originalPrice * 48).toFixed(2)}).
                          <br />
                          Renews at CHF {originalPrice.toFixed(2)}/{getPlanPrice(plan)?.interval || "month"}.
                        </div> */}
                      </>
                    ) : (
                      <>
                        <span className={styles.regularPriceLabel}>
                          CHF/{getDisplayInterval(getPlanPrice(plan)?.interval)}
                        </span>
                        <div className={styles.regularPrice}>
                          CHF{" "}
                          {formatCHF(getPlanPrice(plan)?.amount || plan.price)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </label>
            );
          })
        )}
      </div>

      <div className={styles.unlimitedSection}>
        <div className={styles.unlimitedTitle}>
          unlimited active matchings
        </div>
        <div className={styles.unlimitedPriceContainer}>
          <div className={styles.unlimitedPriceLabel}>
            CHF/month
          </div>
          <Link
            className={styles.unlimitedPriceLink}
            to="/support"
          >
            upon request
          </Link>
        </div>
      </div>
      <div className={styles.subscribeSection}>
        <button
          onClick={handleSubscribe}
          disabled={!selectedPlanId || !selectedPriceId}
          className={styles.subscribeButton}
        >
          {subscribing
            ? "Next"
            : isAuthenticated
              ? "Subscribe Now"
              : "Login to Subscribe"}
        </button>
        {!isAuthenticated && (
          <p className={styles.loginWarning}>
            You must be logged in to subscribe.
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
